import { useSyncExternalStore } from 'react';

/**
 * Creates a singleton hook that shares state and logic across all consumers.
 * The 'startEffect' is only active when at least one component is using the hook.
 * The cleanup effect is automatically called when the hook is no longer in use
 */
export function createSingletonHook<T>(
  initialValue: T,
  // This function is called when the first component mounts.
  // It receives a 'set' function to update the global value.
  // It must return a cleanup function (just like useEffect).
  startEffect: (set: (newValue: T) => void) => () => void
) {
  let globalValue = initialValue;
  const listeners = new Set<() => void>();
  let cleanupEffect: (() => void) | null = null;

  const subscribe = (listener: () => void) => {
    listeners.add(listener);

    // 1. If this is the FIRST listener, start the global effect/observer
    if (listeners.size === 1) {
      cleanupEffect = startEffect((newValue) => {
        // Only trigger updates if the value actually changed
        if (newValue !== globalValue) {
          globalValue = newValue;
          listeners.forEach((l) => l());
        }
      });
    }

    return () => {
      listeners.delete(listener);
      // 2. If this was the LAST listener, tear down the effect
      if (listeners.size === 0 && cleanupEffect) {
        cleanupEffect();
        cleanupEffect = null;
        // Reset to initial to ensure clean slate if re-mounted later
        globalValue = initialValue;
      }
    };
  };

  const getSnapshot = () => globalValue;
  const getServerSnapshot = () => initialValue;

  return () => useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}
